import { useEffect, useMemo, useState } from 'react';
import { useFleetLive } from '@/hooks/useFleetLive';
import { deriveFleetStatus, type FleetJob } from '@/utils/fleetStatus';
import type { MapTech } from '@/components/map/FleetMap';

/** A clock that re-renders every `ms`, so time-based states (off hours, not reporting) move on. */
export function useNow(ms = 30_000, enabled = true): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!enabled) return;
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(id);
  }, [ms, enabled]);
  return now;
}

/**
 * The fleet ready to draw: each active technician's live row, their status derived against
 * today's jobs, and their lane colour. `jobs` must be today's jobs (any filter applied to the
 * map should not hide a technician's own work from the derivation).
 */
export function useFleetView({
  jobs,
  colourOf,
  enabled,
  realtime = true,
}: {
  jobs: FleetJob[];
  colourOf: (techId: string | null) => string;
  enabled: boolean;
  realtime?: boolean;
}) {
  const fleet = useFleetLive({ enabled, realtime });
  const now = useNow(30_000, enabled);

  const entries = useMemo<MapTech[]>(() => {
    const snapshot = fleet.data;
    if (!enabled || !snapshot) return [];
    const byTech = new Map<string, FleetJob[]>();
    for (const job of jobs) {
      if (!job.assigned_tech_id) continue;
      byTech.set(job.assigned_tech_id, [...(byTech.get(job.assigned_tech_id) ?? []), job]);
    }
    return snapshot.techs.map((tech) => ({
      tech,
      status: deriveFleetStatus(tech, byTech.get(tech.tech_id) ?? [], snapshot, now),
      colour: colourOf(tech.tech_id),
    }));
  }, [enabled, fleet.data, jobs, colourOf, now]);

  return { ...fleet, entries, now };
}
